import { Button } from "@9amhealth/shared";
import { useBlocNext } from "@blac/react";
import type { CSSProperties, FC } from "react";
import { useNavigate } from "react-router-dom";
import translate from "src/lib/translate";
import { APP_CHAT_PATH } from "src/lib/createAppPath";
import { BottomSheet } from "src/ui/components/BottomSheet/BottomSheet";
import AvatarStack from "src/ui/components/CareTeamSection/AvatarStack";
import CareTeamDetailCubit from "src/ui/components/CareTeamSection/CareTeamDetailCubit";
import type { CareTeamRole } from "src/ui/components/CareTeamSection/careTeamRoles";
import {
  AlwaysOnPill,
  Body,
  CloseButton,
  Disclaimer,
  Divider,
  Eyebrow,
  Footer,
  Header,
  HeaderTopRow,
  Note,
  NoteText,
  Pill,
  PillList,
  Section,
  Subtitle,
  Title,
  TitleGroup
} from "src/ui/components/CareTeamSection/CareTeamRoleDetailSheet.styled";
import Translate from "src/ui/components/Translate/Translate";

/**
 * Force the sheet surface to plain white regardless of the shared
 * `--dialog-background` fallback (which resolves to `--color-cream`).
 * Passed via `BottomSheet`'s `style` prop so it lands on the modal
 * overlay's CSS custom property scope.
 */
const WHITE_SHEET_STYLE: CSSProperties = {
  "--dialog-background": "#fff"
} as CSSProperties;

const CloseIcon: FC = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 14 14"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      d="M1 1L13 13M13 1L1 13"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
  </svg>
);

const NoteIcon: FC = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden="true"
  >
    <path
      d="M12 8V12M12 16H12.01M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12Z"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

interface DetailContentProps {
  role: CareTeamRole;
  onAskSophie: () => void;
}

/**
 * Presentational body of the detail sheet. Separated from the outer
 * `<BottomSheet>` wrapper so it always receives a non-null role and can
 * render safely during the sheet's exit animation (the wrapper hands it
 * the last-known role while the sheet is closing).
 */
const CareTeamRoleDetailContent: FC<DetailContentProps> = ({
  role,
  onAskSophie
}) => {
  const expertiseTitleKey =
    role.detail.expertiseTitleKey ?? "careTeam.detail.expertiseTitle";

  return (
    <>
      <Header>
        <HeaderTopRow>
          <AvatarStack
            avatars={role.badge.avatars}
            label={role.badge.label}
            size="lg"
            hideLabel={role.detail.headerPillKey !== undefined}
          />
          {role.detail.headerPillKey && (
            <AlwaysOnPill>
              <Translate msg={role.detail.headerPillKey} />
            </AlwaysOnPill>
          )}
        </HeaderTopRow>
        <TitleGroup>
          <Title>
            <Translate msg={role.titleKey} />
          </Title>
          <Subtitle>
            <Translate msg={role.detail.subtitleKey} />
          </Subtitle>
        </TitleGroup>
      </Header>

      <Divider />

      <Section>
        <Eyebrow>
          <Translate msg={expertiseTitleKey} />
        </Eyebrow>
        <Body className="as-body1">
          <Translate msg={role.detail.expertiseBodyKey} />
        </Body>
      </Section>

      <Section>
        <Eyebrow>
          <Translate msg="careTeam.detail.whereTheyHelpTitle" />
        </Eyebrow>
        <PillList>
          {role.detail.whereTheyHelpKeys.map((key) => (
            <Pill key={key}>
              <Translate msg={key} />
            </Pill>
          ))}
        </PillList>
      </Section>

      {role.detail.noteKey && (
        <Note className="as-little">
          <NoteIcon />
          <NoteText>
            <Translate msg={role.detail.noteKey} />
          </NoteText>
        </Note>
      )}

      <Section>
        <Eyebrow>
          <Translate msg="careTeam.detail.howToBenefitTitle" />
        </Eyebrow>
        <Body className="as-body1">
          <Translate msg={role.detail.howToBenefitBodyKey} />
        </Body>
      </Section>

      <Footer>
        <Button theme="sunrise" grow onPress={onAskSophie}>
          <Translate msg="careTeam.detail.ctaAskSophie" />
        </Button>
        <Disclaimer className="as-caption">
          {role.detail.disclaimerKey && (
            <Translate msg={role.detail.disclaimerKey} />
          )}
        </Disclaimer>
      </Footer>
    </>
  );
};

/**
 * Renders the Care Team detail sheet driven by `CareTeamDetailCubit`.
 * `displayedRole` is preserved by the cubit during the sheet's exit
 * animation so content stays stable while the sheet slides away.
 */
const CareTeamRoleDetailSheet: FC = () => {
  const [{ activeRoleId, displayedRole }, { close }] =
    useBlocNext(CareTeamDetailCubit);
  const navigate = useNavigate();

  const isOpen = activeRoleId !== null;

  const handleAskSophie = (): void => {
    close();
    navigate(APP_CHAT_PATH);
  };

  return (
    <BottomSheet
      isOpen={isOpen}
      onClose={close}
      trackingName="Care Team Role Detail"
      trackingData={{ roleId: displayedRole?.id ?? "" }}
      desktopFullscreen
      style={WHITE_SHEET_STYLE}
    >
      <CloseButton
        type="button"
        onClick={close}
        aria-label={translate("careTeam.detail.close")}
      >
        <CloseIcon />
      </CloseButton>
      {displayedRole && (
        <CareTeamRoleDetailContent
          role={displayedRole}
          onAskSophie={handleAskSophie}
        />
      )}
    </BottomSheet>
  );
};

export default CareTeamRoleDetailSheet;
